/**
 * WVM (Wood Viewer Model) — hợp đồng dữ liệu dòng v1.x.
 * Nguồn sự thật: design/schema/wvm-1.0.schema.json (Draft 2020-12).
 * Viewer chỉ tiêu thụ WVM; không phụ thuộc nguồn CAD (bazis/sketchup/...).
 */

export type WvmSource = 'bazis' | 'sketchup';

export type GeometryKind = 'box' | 'cylinder' | 'mesh';

/** Loại chuyển động (kế thừa nguyên từ bản thử nghiệm). 0 = cố định. */
export type SalonType = 0 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 11 | 12 | 13 | 14 | 15;

export const SALON_TYPES: readonly SalonType[] = [0, 2, 3, 4, 5, 6, 7, 8, 11, 12, 13, 14, 15];

export interface WvmModel {
  name: string;
  unit: string; // "mm"
  up: string; // "y" (BAZIS)
  /** id model gốc từ CAD → backend dùng làm external_ref (re-upload giữ token). */
  sourceId?: string;
}

export interface WvmMaterial {
  id: string;
  name: string;
  /** hex #rrggbb */
  color: string;
}

/** Lỗ khoét (chỉ box). Tọa độ trong không gian model (mm). */
export interface WvmCutout {
  wx: number;
  hy: number;
  dz: number;
  cx: number;
  cy: number;
  cz: number;
}

/** Tấm (BAZIS): X rộng, Y cao, Z sâu; tâm cx/cy/cz. */
export interface BoxGeometry {
  kind: 'box';
  wx: number;
  hy: number;
  dz: number;
  cx: number;
  cy: number;
  cz: number;
  /** bán kính bo góc (mm). */
  round?: number;
  cutouts?: WvmCutout[];
}

/** Fastener trụ (BAZIS, v1.1): chốt/cam/vít. */
export interface CylinderGeometry {
  kind: 'cylinder';
  radius: number;
  height: number;
  axis: 'x' | 'y' | 'z';
  cx: number;
  cy: number;
  cz: number;
}

/** Mesh nặng (SketchUp): glTF/Draco qua uri + ma trận 16 số. */
export interface MeshGeometry {
  kind: 'mesh';
  /** đường dẫn tương đối trong package (vd "assets/part-1.glb"). */
  uri: string;
  /** ma trận 4x4 dạng phẳng (16 số). */
  transform: number[];
}

/** Mesh NHÚNG (BAZIS, vd đan mây): triangle soup world-space mm. */
export interface MeshInlineGeometry {
  kind: 'mesh';
  /** [x,y,z,...] world mm, len % 9 == 0. KHÔNG có transform (đã nhân GlobalMatrix). */
  positions: number[];
  /** (tuỳ chọn) cùng độ dài positions; vắng → tự computeVertexNormals. */
  normals?: number[];
  /** (tuỳ chọn) UV per-vertex [u,v,...], ĐƠN VỊ mm (BAZIS TexCoord), len = positions/3*2.
   *  Viewer lát texture theo mm (repeat = 1/repeatMm) như box. Vắng → chỉ màu trơn. */
  uvs?: number[];
}

/**
 * 04-Đòn C (WVM 1.5): dữ liệu lưới LOCAL dùng chung cho nhiều instance (phụ kiện lặp: bản lề, chân…).
 * Lưu 1 lần trong `WvmDocument.meshAssets`; mỗi instance là part có geometry `mesh-ref` trỏ `assetId`.
 * Toạ độ LOCAL mm (CHƯA nhân GlobalMatrix) → transform ở part đặt vào world.
 */
export interface MeshAsset {
  /** [x,y,z,...] LOCAL mm, len % 9 == 0. */
  positions: number[];
  /** (tuỳ chọn) cùng độ dài positions, LOCAL unit; vắng → computeVertexNormals. */
  normals?: number[];
  /** (tuỳ chọn) UV per-vertex mm, len = positions/3*2. */
  uvs?: number[];
}

/** Mesh THAM CHIẾU (BAZIS, Đòn C): trỏ `meshAssets[assetId]` + ma trận đặt instance. */
export interface MeshRefGeometry {
  kind: 'mesh';
  /** → khoá trong `WvmDocument.meshAssets`. Phân biệt với mesh-inline (có positions) / mesh-uri (có uri). */
  assetId: string;
  /** ma trận 4x4 phẳng (16 số, column-major) = GlobalMatrix instance: LOCAL mm → world mm. Rigid (không scale méo/mirror ở v1). */
  transform: number[];
}

/**
 * Panel UỐN (BAZIS bent): đùn mặt cắt 2D đã đóng kín (đã tessellate cung + nở bề dày)
 * dọc +Z từ 0..height, rồi đặt vào world bằng transform 4x4 (column-major).
 */
export interface ExtrusionGeometry {
  kind: 'extrusion';
  /** đa giác mặt cắt KÍN trong mặt phẳng đùn (mm): [[x,y], ...]. */
  shape: [number, number][];
  /** chiều dài đùn (mm). */
  height: number;
  /** ma trận 4x4 phẳng (16 số, column-major) đặt (shapeXY + đùn Z) vào world. */
  transform: number[];
}

export type WvmGeometry =
  | BoxGeometry
  | CylinderGeometry
  | ExtrusionGeometry
  | MeshGeometry
  | MeshInlineGeometry
  | MeshRefGeometry;

export interface WvmMotion {
  salonType: SalonType;
  /** nhóm ngăn kéo (0 = không thuộc nhóm). */
  drawerGroup?: number;
  flip?: boolean;
  /**
   * Chiều mở THEO CHIỀU SÂU (ra/vào) cho cửa bản lề đứng (salonType 2/3) — TRỰC GIAO với `flip`
   * (`flip` = cạnh bản lề trái/phải). `MotionEngine.ROT` mặc định cho cạnh tự do quét về +Z; đặt
   * `swingOut=true` → đảo dấu góc để quét −Z (tủ đặt "quay 180°", mặt trước hướng −Z). VẮNG ⇒ +Z
   * như cũ (tương thích ngược tuyệt đối — mọi model "đúng" hiện tại là tủ mặt +Z). Author-mode gán.
   */
  swingOut?: boolean;
  /**
   * Vector trượt WORLD (mm) ở trạng thái mở hoàn toàn — đọc thẳng từ TFurnAnimation (AnimType=2).
   * Khi có, viewer trượt part theo vector này, BỎ heuristic `salonType`. Dùng cho phụ kiện
   * (giỏ/vắt quần "GTV Frame Slide") mà `salonType=0` + là mesh đơn (không suy được hướng từ hình học).
   */
  slideVec?: { x: number; y: number; z: number };
  /**
   * Xoay quanh trục WORLD tuỳ ý — đọc thẳng từ TFurnAnimation (AnimType=1). `pivot` = điểm trên trục (mm),
   * `axis` = hướng đơn vị, `angle` = góc (độ) tại mở hoàn toàn. Khi có, viewer xoay part quanh trục này,
   * BỎ heuristic. Dùng cho phụ kiện lật (kệ giày "Обувная система"). Loại trừ lẫn nhau với `slideVec`.
   */
  rotate?: { pivot: { x: number; y: number; z: number }; axis: { x: number; y: number; z: number }; angle: number };
}

export type PartCategory = 'panel' | 'door' | 'hardware';

/** Hướng vân vật liệu trên tấm (map BAZIS Panel.TextureOrientation). Viewer dùng xoay texture. */
export type TextureOrientation = 'none' | 'horizontal' | 'vertical';

/** 04a/02a: một material NẸP (edge band) phân biệt trong model. KHÔNG chứa màu (màu là thiết lập user). */
export interface BandMaterial {
  /** id ổn định TRONG 1 file WVM (b0, b1…). */
  id: string;
  /** TÊN vật liệu nẹp — định danh thật, ổn định + DUY NHẤT trong model (khoá giữ màu ở backend). */
  name: string;
  /** mm — bề dày nẹp (phần lồi ra). */
  thickness: number;
  /** mm — bề rộng phủ cạnh. */
  width: number;
}

/** 04a/02a: dải nẹp của 1 part — ruy-băng đã tam-giác-hoá, CÙNG không gian `part.geometry` (mm). */
export interface EdgeBand {
  /** → BandMaterial.id. */
  materialId: string;
  /** triangle soup, bội số 9 float (mm, model-space). */
  positions: number[];
  /** OPTIONAL, cùng độ dài `positions`; thiếu → viewer tự tính flat. */
  normals?: number[];
}

/**
 * 04c/08a/03a: RÃNH XOI (groove/паз) do plugin trích từ `panel.Cuts` dạng đường chạy.
 * `path` = polyline TÂM rãnh trên mặt mở, WORLD mm (arc đã chia đoạn bởi plugin). `width` = bề rộng
 * ⊥ đường chạy. `normal` = vector đơn vị world = pháp tuyến mặt mở (chiều ăn vào vật liệu) — viewer
 * dùng THẲNG, không suy từ hộp. `depth` = dữ liệu (v1 vẽ phẳng "đường khắc", chưa lõm thật). `face`/
 * `name` = hiển thị/lọc. Gắn dưới pivot part như edgeBands → trượt theo motion.
 */
export interface Groove {
  path: [number, number, number][];
  width: number;
  depth: number;
  normal: [number, number, number];
  face?: 'front' | 'back';
  name?: string;
}

export interface WvmPart {
  id: number;
  /** mã CT (số thứ tự cắt). */
  code?: string;
  /** mã phân cấp BAZIS (= số trên tem QR), duy nhất toàn model. Optional. */
  designation?: string;
  name: string;
  /** cụm/nhóm logic (dùng gom trong danh sách). */
  module?: string;
  category?: PartCategory;
  materialId?: string;
  geometry: WvmGeometry;
  /** tùy chọn / nullable — null nếu chưa gán chuyển động. */
  motion?: WvmMotion | null;
  /** hướng vân (optional; thiếu = "none"). Viewer dùng để xoay texture, không ảnh hưởng hình học. */
  textureOrientation?: TextureOrientation;
  /** 04a/02a: dải nẹp cạnh của panel (optional; WVM cũ vắng → không render nẹp). */
  edgeBands?: EdgeBand[];
  /** 04c: rãnh xoi của panel (optional; WVM cũ vắng → không vẽ rãnh). */
  grooves?: Groove[];
}

export interface WvmExporter {
  name: string;
  version: string;
}

/** Preset ánh sáng mức model (tác giả chọn, áp cho mọi người xem). */
export type LightingPreset = 'even' | 'studio' | 'bright';

/**
 * Tham số ánh sáng MỨC MODEL (display setting, không phải hình học CAD). Tác giả chỉnh trong
 * Author mode + lưu; backend merge vào payload để mọi người xem thấy. Vắng = mặc định viewer.
 * Lưu trữ/endpoint: xem design/03h-backend-lighting-persist.md.
 */
export interface WvmLighting {
  preset: LightingPreset;
  /** false = chế độ phẳng "không hiệu ứng" (không bóng/khối/loé), KHÔNG phải tối. */
  on: boolean;
  /** cường độ tổng 0..100 (50 = mức chuẩn preset). */
  intensity: number;
}

/**
 * 04b/08h/03v: đường kích thước do user DỰNG trong plugin (BAZIS/SketchUp), KHÔNG phải khung bao auto.
 * Toạ độ cùng hệ world với `parts` (mm). `value` đã là mm (lấy thẳng). `text` có → dùng làm nhãn.
 * `offset` (optional, v1 plugin chưa gửi) = vector world mm dịch đường gióng khỏi cạnh đo.
 */
export interface Dimension {
  type: 'linear';
  p1: [number, number, number];
  p2: [number, number, number];
  value: number;
  text?: string;
  offset?: [number, number, number];
}

export interface WvmDocument {
  /** pattern ^1\.\d+$ — viewer kiểm major để tương thích. */
  wvmVersion: string;
  source: WvmSource;
  exportedAt?: string;
  exporter?: WvmExporter;
  model: WvmModel;
  materials?: WvmMaterial[];
  /** 04a/02a: danh sách material nẹp phân biệt (optional; vắng/rỗng = model không có nẹp). */
  bandMaterials?: BandMaterial[];
  parts: WvmPart[];
  /** 04-Đòn C (WVM 1.5): kho lưới LOCAL dùng chung cho part `mesh-ref`. Vắng = model không có instance gộp. */
  meshAssets?: Record<string, MeshAsset>;
  /** ánh sáng mức model (tác giả lưu; backend merge). Vắng = mặc định viewer (even/on/50). */
  lighting?: WvmLighting;
  /** 04b: đường kích thước user dựng (plugin xuất). Vắng/rỗng = không vẽ gì (model cũ). */
  dimensions?: Dimension[];
}

// ---------- type guards tiện dụng ----------

export const isBox = (g: WvmGeometry): g is BoxGeometry => g.kind === 'box';
export const isCylinder = (g: WvmGeometry): g is CylinderGeometry => g.kind === 'cylinder';
export const isExtrusion = (g: WvmGeometry): g is ExtrusionGeometry => g.kind === 'extrusion';
/** mesh-uri (SketchUp .glb): có `uri`. */
export const isMesh = (g: WvmGeometry): g is MeshGeometry =>
  g.kind === 'mesh' && typeof (g as MeshGeometry).uri === 'string';
/** mesh-inline (BAZIS đan mây): có `positions`. */
export const isMeshInline = (g: WvmGeometry): g is MeshInlineGeometry =>
  g.kind === 'mesh' && Array.isArray((g as MeshInlineGeometry).positions);
/** mesh-ref (Đòn C): có `assetId`, KHÔNG positions/uri. */
export const isMeshRef = (g: WvmGeometry): g is MeshRefGeometry =>
  g.kind === 'mesh' &&
  typeof (g as MeshRefGeometry).assetId === 'string' &&
  !Array.isArray((g as MeshInlineGeometry).positions);
